import { useQuery } from '@apollo/client';
import { AddCircle, VerifiedUser } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import type { GridRowId, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { RouterBreadcrumbs } from '~/components/RouterBreadcrumbs';
import { ScrollableRouterTabs } from '~/components/RouterTabs';
import { SearchBar } from '~/components/forms/search-bar';
import { PageContainer } from '~/components/page-layout';
import { SearchContainer } from '~/components/search';
import { Toolbar } from '~/components/toolbar';
import { useAppContext } from '~/contexts';
import { useTabs } from '../lib/tabs';
import { RoleDeviceListDocument } from './RoleDeviceList.generated';
import { AddDevicesDialog } from './components/AddDevicesDialog';
import { BulkActionsMenu } from './components/BulkActionsMenu';
import { DeviceTable } from './components/DeviceTable';
import { MultiCount } from './components/MultiCount';

export const RoleDeviceList = () => {
  const { t } = useTranslation();
  const params = useParams<{ roleId: string }>();
  const roleId = parseInt(params.roleId ?? '');

  const { currentNetwork } = useAppContext();
  const tabs = useTabs();

  const [searchParams] = useSearchParams();
  const search = searchParams.get('search')?.trim() ?? '';

  const { data, loading, error } = useQuery(RoleDeviceListDocument, {
    variables: { networkId: currentNetwork.id, roleId, search },
  });

  const [addDevicesOpen, setAddDevicesOpen] = useState(false);

  const [selectedIds, setSelectedIds] = useState<GridRowSelectionModel>([]);

  const rules = useMemo(
    () => [
      ...(data?.network?.role.deviceGroupRules ?? []),
      ...(data?.network?.role.deviceRules ?? []),
    ],
    [data?.network?.role.deviceGroupRules, data?.network?.role.deviceRules],
  );

  // Update selection if data changes...this seems overly complicated but works
  useEffect(() => {
    if (!data?.network?.role) return;
    const ruleIds = new Set<GridRowId>(rules.map((rule) => `${rule.__typename}:${rule.id}`));
    if (selectedIds.every((id) => ruleIds.has(id))) return;
    setSelectedIds(selectedIds.filter((id) => ruleIds.has(id)));
  }, [data?.network?.role, rules, selectedIds]);

  if ((!loading && !data) || error) return <Navigate replace to="/not-found" />;

  const addButton = (
    <Button
      color="primary"
      onClick={() => setAddDevicesOpen(true)}
      startIcon={<AddCircle />}
      variant="contained"
    >
      {t('pages.roles.buttons.add_devices')}
    </Button>
  );

  return (
    <>
      {data?.network && (
        <>
          <Toolbar
            actions={addButton}
            breadcrumbsLabel={<RouterBreadcrumbs />}
            titleIcon={<VerifiedUser />}
            titleText={data.network.role.name}
          />

          <ScrollableRouterTabs current={t('common.labels.devices')} tabs={tabs} />

          <PageContainer>
            <SearchContainer>
              <SearchBar
                placeholder={t('pages.roles.devices.search_placeholder')}
                search={search}
              />
              <Box sx={{ alignItems: 'center', display: 'flex', gap: 4 }}>
                <BulkActionsMenu role={data.network.role} selectedIds={selectedIds} />
                <MultiCount
                  descriptors={[
                    {
                      matchesItem: (item) => item.__typename === 'DeviceGroupRule',
                      matchesSelectedId: (id) => String(id).startsWith('DeviceGroupRule:'),
                      name: 'pages.roles.devices.device_group_thing_plural',
                    },
                    {
                      matchesItem: (item) => item.__typename === 'DeviceRule',
                      matchesSelectedId: (id) => String(id).startsWith('DeviceRule:'),
                      name: 'pages.roles.devices.device_thing_plural',
                    },
                  ]}
                  items={rules}
                  selectedIds={selectedIds}
                />
              </Box>
            </SearchContainer>
            <DeviceTable
              checkboxSelection={rules.length > 1}
              disableRowSelectionOnClick={rules.length < 2}
              loading={loading}
              onRowSelectionModelChange={setSelectedIds}
              role={data.network.role}
              rows={rules}
              rowSelectionModel={selectedIds}
            />
          </PageContainer>

          {addDevicesOpen && (
            <AddDevicesDialog
              deviceGroups={data.network.allDeviceGroups}
              devices={data.network.ungroupedDevices}
              fullWidth
              onClose={() => setAddDevicesOpen(false)}
              open
              role={data.network.role}
            />
          )}
        </>
      )}
    </>
  );
};
